// libs
import { connect } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

// redux selectors
import {
  getColorOptions,
  getDefaultColor,
  getDeliveryDetails,
  getDisclaimerText,
  getProductData,
  getProductDiscountPrice,
  getProductId,
  getProductParentSKU,
  getProductPrice,
  getProductSizeOptions,
  getProductSubTitle,
  getProductTitle,
  getReponsibleTagsAvailable,
  getReviewSummary,
  getSizeGuideURL,
  getTagTitles,
  isOneSize,
  isReturnable as _isReturnable,
  isVideoAvailableOnPDP,
  getProductDiscount,
  getCategoryLevel,
  getSelectedSizeName,
  getSelectedSKUID,
  getBadge,
  getUrgencyNudge,
  hasViewSimilar,
  getSelectedSKUDataPDPSections,
  getHeroImageData,
  getModelWidgetData,
  getIsSizeGuideDataAvailable,
  getBrandBook,
  getProductInventoryNudge,
  getSpecialTagsInfo,
  isSaleParticipating,
  getLowReturnNudge,
} from 'shared/store/details/selectors';
import { getABExperimentVariant } from '@nykaa/experiment-sdk/store/selectors';
import { getRemoteConfig } from 'shared/store/remoteConfigs/selectors';
import { isPostRequestAllowed } from 'shared/store/app/selectors';
import { getPdpViewInteractionLocation } from 'shared/helpers/analytics/pdpViewSource';

// component
import Analytics from './Analytics';
import { DEFAULT_CURRENCY_CODE } from 'shared/constants';

// helpers
import { isDateValid } from 'shared/helpers/utils';
import { getSelectedVendorId, resolveVendorId } from 'shared/helpers/multiSeller';

// constants
import {
  GAMIFICATION_NUDGE,
  GAMIFICATION_NUDGE_VARIANT_A,
} from 'shared/components/GamificationNudge/constants';
import {
  LOW_RETURN_NUDGE_EXPERIMENT,
  LOW_RETURN_NUDGE_VARIANTS,
} from 'shared/layouts/DetailsPage/constants';

const mapStateToProps = (state: any, ownProps: any) => {
  const {
    color,
    type,
    isOutOfStock,
    imageUrl,
    categoryName = '',
    price_reveal_data = {},
    fomo_tag = {},
    sale_price_live_flag = false,
  } = getProductData(state);
  const product = getProductData(state);
  const selectedSKU = get(state, 'details.selectedSKU', product);
  const selectedVendor = selectedSKU || product;
  const vendors = selectedVendor?.view_more_section?.vendors || {};
  const selectedVendorId = resolveVendorId(
    vendors,
    getSelectedVendorId(selectedSKU, product?.vendor_id),
    product?.vendor_id
  );
  const selectedVendorDetails = vendors[selectedVendorId] as ProductDetail.IVendorDetails;
  const availableVendors = Object.entries(vendors) as [string, ProductDetail.IVendorDetails][];
  const { isMobile } = ownProps;
  const isPriceReveal =
    get(getRemoteConfig(state, 'saleExperience'), 'priceReveal.enabled', false) ||
      get(getRemoteConfig(state, 'priceReveal'), isMobile ? 'mobile' : 'desktop', false)
      ? !!price_reveal_data?.sp
      : undefined;

  const isSalePriceLiveEnabled =
    get(
      getRemoteConfig(state, `priceReveal`).salePromotionConfig,
      isMobile ? 'mobile' : 'desktop',
      false
    ) || get(getRemoteConfig(state, `saleExperience`).saleCommunication, 'pdp.enabled', false);

  const isSalePriceLive = isSalePriceLiveEnabled && sale_price_live_flag;

  const isFomoTimerVisible = get(getRemoteConfig(state, 'fomoTimer'), 'pdp.enable', false)
    ? isDateValid(fomo_tag?.end_time)
    : undefined;

  const { isEligible: isGamificationNudgeEnabled = false, variant: gamificationNudgeVariant = '' } =
    getABExperimentVariant(state, GAMIFICATION_NUDGE);
  const { isEligible: isLowReturnNudgeEligible = false, variant: lowReturnNudgeVariant = '' } =
    getABExperimentVariant(state, LOW_RETURN_NUDGE_EXPERIMENT);
  return {
    waitForOffers: getPdpViewInteractionLocation() === 'cartPage' &&
      isPostRequestAllowed(state) && !ownProps.isCategoryLaunchingSoon,
    productID: getProductId(state),
    title: getProductTitle(state),
    subTitle: getProductSubTitle(state),
    sizeOptions: getProductSizeOptions(state),
    parentSKU: getProductParentSKU(state),
    isOneSize: isOneSize(state),
    price: getProductPrice(state),
    discountedPrice: getProductDiscountPrice(state),
    tags: getTagTitles(state),
    review: getReviewSummary(state),
    isSizeGuideAvailable: getSizeGuideURL(state),
    colorOptions: getColorOptions(state),
    defaultColor: getDefaultColor(state),
    isVideoAvailable: isVideoAvailableOnPDP(state),
    disclaimerText: getDisclaimerText(state),
    areResponsibleTagsAvailable: getReponsibleTagsAvailable(state),
    colorConfig: color?.name || '',
    type,
    isOutOfStock,
    imageUrl,
    deliveryDetails: getDeliveryDetails(state),
    isProductReturnable: _isReturnable(state),
    categoryName,
    isPriceReveal,
    currency: DEFAULT_CURRENCY_CODE,
    discountPercentage: getProductDiscount(state),
    categoryLevels: getCategoryLevel(state),
    selectedSizeName: getSelectedSizeName(state),
    selectedSKUID: getSelectedSKUID(state),
    productBadge: getBadge(state)?.title,
    urgencyNudgeData: getUrgencyNudge(state),
    urgencyNudgeEnabled: get(
      getRemoteConfig(state, 'urgencyNudge'),
      isMobile ? 'pdp.mweb' : 'pdp.dweb',
      {
        mweb: false,
        dweb: false,
        textColor: '',
      }
    ),
    isFomoTimerVisible,
    isSalePriceLive,
    isViewSimilarAvailable: hasViewSimilar(state),
    pdpSections: getSelectedSKUDataPDPSections(state),
    isAplusContentAvailable: !isEmpty(getHeroImageData(state)),
    isModelDetailsAvailable: !isEmpty(getModelWidgetData(state)),
    isNewSizeChartEnabled: getRemoteConfig(state, 'newSizeChartEnabled'),
    isSizeChartDataAvailable: getIsSizeGuideDataAvailable(state),
    isBrandBookData: getBrandBook(state),
    isGamificationEnabled: isGamificationNudgeEnabled,
    gamificationNudgeVariant: gamificationNudgeVariant,
    isGamificationAggresive:
      isGamificationNudgeEnabled && gamificationNudgeVariant === GAMIFICATION_NUDGE_VARIANT_A
        ? 0
        : 1,
    inventoryNudge: getProductInventoryNudge(state),
    isSpecialTagsPresent: getSpecialTagsInfo(state).isSpecialTagsPresent,
    specialTagsName: getSpecialTagsInfo(state).specialTagsName,
    specialTag: getSpecialTagsInfo(state).specialTag,
    isSaleParticipating: isSaleParticipating(state),
    returnNudgeApplicable:
      !!getLowReturnNudge(state),
    returnNudgeVisible:
      !!getLowReturnNudge(state) &&
      !!getRemoteConfig(state, 'lowReturnNudge')?.enabled &&
      isLowReturnNudgeEligible &&
      LOW_RETURN_NUDGE_VARIANTS.includes(lowReturnNudgeVariant),
    catalog_tag: getProductData(state)?.catalog_tag,
    selected_vendor_code: selectedVendorId,
    selected_vendor_type:
      selectedVendorDetails?.vendor_type || selectedSKU?.vendor_type || product?.vendor_type,
    available_vendor_code: availableVendors.map(([vendorId]) => vendorId),
    available_vendor_type: availableVendors.map(([, vendor]) => vendor.vendor_type),
    available_vendor_count: availableVendors.length,
  };
};

export default connect(mapStateToProps)(Analytics);
